import styled from "@emotion/styled";
import { Flex, Text } from "styles/utility-components";

import Button from "components/ui/button/Button";
import { ANNOUNCEMENT_CARD, useVariableValue } from "lib/featureFlags";
import { useLocalStorage } from "lib/hooks/useLocalStorage";
import UIcon from "lib/icons/untitled_ui/UIcon";
import {
  Announcement,
  announcementSchema,
  BasicAnnouncement,
  WebinarAnnouncement,
} from "./announcementSchema";
import { formatWebinarDate } from "./formatWebinarDate";

/**
 * Floating annoucement card which will be displayed on the bottom left of the page
 * when the connected DevCycle feature flag is enabled. Once dismissed, the card will
 * not show again for that annoucement ID in the current browser
 */
export function AnnouncementCard(): JSX.Element | null {
  const raw = useVariableValue(ANNOUNCEMENT_CARD, {
    enabled: false,
    type: "basic",
    id: "default",
    title: "",
  });

  const parsed = announcementSchema.safeParse(raw);
  if (!parsed.success || parsed.data.enabled === false) return null;

  const announcement = parsed.data;

  if (announcement.type === "webinar") {
    return <WebinarAnnouncementCard key={announcement.id} announcement={announcement} />;
  }

  return <BasicAnnouncementCard key={announcement.id} announcement={announcement} />;
}

export function WebinarAnnouncementCard({
  announcement,
}: {
  announcement: WebinarAnnouncement;
}): JSX.Element | null {
  return (
    <AnnouncementCardShell
      announcement={announcement}
      dateLabel={formatWebinarDate(announcement.webinarDate)}
      mediaFallback={
        <Placeholder
          column
          alignItems="center"
          justifyContent="center"
          gap={8}
          height={180}
          width="100%"
        >
          <UIcon icon="monitor01" size={24} color="sysPrimaryDefault" />
          <Text fontWeight="700" color="sysPrimaryDefault">
            Upcoming webinar
          </Text>
        </Placeholder>
      }
    />
  );
}

/**
 * Announcement with no date and no media band of its own. Used for anything that is
 * not a webinar, so it shows nothing above the title unless an image is supplied.
 */
export function BasicAnnouncementCard({
  announcement,
}: {
  announcement: BasicAnnouncement;
}): JSX.Element | null {
  return <AnnouncementCardShell announcement={announcement} />;
}

function AnnouncementCardShell({
  announcement,
  dateLabel,
  mediaFallback,
}: {
  announcement: Announcement;
  dateLabel?: string | null;
  mediaFallback?: JSX.Element;
}): JSX.Element | null {
  const [dismissed, setDismissed] = useLocalStorage(`hideAnnouncement:${announcement.id}`, false);

  if (dismissed) return null;

  const { title, subline, imageUrl, action } = announcement;
  const media = imageUrl ? <Cover src={imageUrl} alt="" /> : mediaFallback;
  // With no media band above it the dismiss button floats over the first line of
  // the title, so the text has to leave room for it.
  const TextColumn = media ? TextBlock : TextBlockClearingDismiss;

  return (
    <Card>
      {media}

      <DismissWrap>
        <Button
          as="button"
          buttonType="secondary"
          icon="xClose"
          iconProps={{ color: "sysTextSecondary" }}
          onClick={() => setDismissed(true)}
          aria-label="Dismiss announcement"
        />
      </DismissWrap>

      <Body column alignItems="flex-start" gap={24} width="100%">
        <TextColumn column gap={8} width="100%">
          <Text h2 lineHeight={1.25}>
            {title}
          </Text>
          {subline && (
            <Text lineHeight={1.5} data-testid="announcement-subline">
              {subline}
            </Text>
          )}
          {dateLabel && (
            <Text color="sysTextSecondary" lineHeight={1.5}>
              {dateLabel}
            </Text>
          )}
        </TextColumn>
        {action && (
          <Button as="a" href={action.url} target="_blank" icon={action.icon}>
            {action.text}
          </Button>
        )}
      </Body>
    </Card>
  );
}

// --- Styles ---

const Card = styled.div(({ theme }) => ({
  position: "fixed",
  bottom: 16,
  left: 16,
  width: 320,
  zIndex: 100, // above page content + side nav (z6), below the top-layer portal (z9999) so slideouts cover it
  display: "flex",
  flexDirection: "column",
  backgroundColor: theme.colors.sysBackgroundDefault,
  border: `1px solid ${theme.colors.sysBorderDecorative}`,
  borderRadius: 8,
  overflow: "hidden",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.12)",
  "@media (max-width: 768px)": {
    display: "none",
  },
}));

const Cover = styled.img({
  display: "block",
  width: "100%",
  aspectRatio: "16 / 9",
  objectFit: "cover",
});

const Placeholder = styled(Flex)(({ theme }) => ({
  backgroundColor: theme.colors.sysPrimarySubtle,
}));

const DismissWrap = styled.div({
  position: "absolute",
  top: 12,
  right: 12,
});

const Body = styled(Flex)({
  padding: 24,
});

const TextBlock = styled(Flex)({
  wordBreak: "break-word",
});

const TextBlockClearingDismiss = styled(TextBlock)({
  paddingRight: 24,
});

export default AnnouncementCard;
